import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faLocationArrow, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { useBranches, useShopInfo } from '../../settings/SettingsContext';
import { BranchSummary } from '../../data/fetchSettings';
import { formatKm, LatLng, sortByDistance } from '../../branch/geo';
import { displayName, withLang } from '../../branch/name';
import { brandName, lastBranch } from '../../branch/head';
import LanguageSwitcher from '../LanguageSwitcher';
import HeroMedia from './HeroMedia';
import LogoTile from './LogoTile';
import OpenStatus from './OpenStatus';
import './branch.css';

const SEARCH_FROM = 9; // a filter box once the list is longer than 8

const T = {
  gr: {
    stores: (n: number) => (n === 1 ? '1 κατάστημα' : `${n} καταστήματα`),
    choose: 'Επιλέξτε κατάστημα',
    continueAt: 'Συνέχεια στο κατάστημα',
    nearest: 'Πλησιέστερο σε μένα',
    locating: 'Εντοπισμός θέσης…',
    byDistance: 'Τα καταστήματα είναι ταξινομημένα με βάση την απόσταση.',
    denied: 'Η πρόσβαση στην τοποθεσία δεν επιτράπηκε. Τα καταστήματα εμφανίζονται αλφαβητικά.',
    unavailable: 'Δεν βρέθηκε η τοποθεσία σας. Τα καταστήματα εμφανίζονται αλφαβητικά.',
    search: 'Αναζήτηση καταστήματος',
    searchHint: 'Περιοχή ή διεύθυνση',
    none: (q: string) => `Κανένα κατάστημα για «${q}». Δοκιμάστε μια περιοχή ή οδό.`,
  },
  en: {
    stores: (n: number) => (n === 1 ? '1 store' : `${n} stores`),
    choose: 'Choose a store',
    continueAt: 'Continue at',
    nearest: 'Nearest to me',
    locating: 'Finding you…',
    byDistance: 'Stores are sorted by distance.',
    denied: 'Location access was not allowed. Stores are listed alphabetically.',
    unavailable: 'Your location could not be found. Stores are listed alphabetically.',
    search: 'Search stores',
    searchHint: 'Area or street',
    none: (q: string) => `No store matches “${q}”. Try an area or a street.`,
  },
};

/** Lowercase, no accents, final sigma folded: «Κολωνάκι» and «κολωνακι» match. */
function foldText(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/ς/g, 'σ');
}

type Geo =
  | { status: 'idle' }
  | { status: 'locating' }
  | { status: 'located'; here: LatLng }
  | { status: 'failed'; denied: boolean };

interface BrandPickerProps {
  lang: 'gr' | 'en';
  onLanguageChange: (lang: 'gr' | 'en') => void;
  /** Clock for the open-now labels (tests pass a fixed one). */
  now?: Date;
}

/**
 * Brand root of a franchise (`/` when the shop has branches): the brand hero, then every
 * active branch as a plain link to `/<slug>` (crawlable; a full load enters the branch).
 */
const BrandPicker: React.FC<BrandPickerProps> = ({ lang, onLanguageChange, now }) => {
  const t = T[lang];
  const info = useShopInfo();
  const branches = useBranches();
  const brand = brandName(info);
  const [query, setQuery] = useState('');
  const [geo, setGeo] = useState<Geo>({ status: 'idle' });
  // ponytail: open-now is computed per render, not ticking; a guest spends seconds on this screen.
  const clock = now ?? new Date();

  const last = useMemo(() => {
    const slug = lastBranch();
    return branches.find((b) => b.slug === slug) || null;
  }, [branches]);

  const canLocate =
    typeof navigator !== 'undefined' && 'geolocation' in navigator &&
    branches.some((b) => typeof b.lat === 'number' && typeof b.lng === 'number');

  const listed: Array<BranchSummary & { distanceKm?: number }> = useMemo(() => {
    const ordered = geo.status === 'located' ? sortByDistance(branches, geo.here)
      : [...branches].sort((a, b) => displayName(a, lang).localeCompare(displayName(b, lang), lang === 'en' ? 'en' : 'el'));
    const q = foldText(query.trim());
    if (!q) return ordered;
    return ordered.filter((b) => foldText(`${b.name} ${b.nameEn || ''} ${b.addressText || ''}`).includes(q));
  }, [branches, geo, query, lang]);

  const locate = () => {
    if (geo.status === 'located') {
      setGeo({ status: 'idle' }); // second tap: back to alphabetical
      return;
    }
    setGeo({ status: 'locating' });
    navigator.geolocation.getCurrentPosition(
      (pos) => setGeo({ status: 'located', here: { lat: pos.coords.latitude, lng: pos.coords.longitude } }),
      (err) => setGeo({ status: 'failed', denied: err.code === 1 }),
      { timeout: 8000, maximumAge: 5 * 60 * 1000 },
    );
  };

  const message =
    geo.status === 'located' ? t.byDistance
      : geo.status === 'failed' ? (geo.denied ? t.denied : t.unavailable)
        : '';

  return (
    <main id="main-content" className="brand-picker">
      <header className="bp-hero">
        <HeroMedia videoUrl={info.heroVideoUrl} imageUrl={info.heroUrl} lang={lang} />
        <div className="bp-hero__top">
          <LanguageSwitcher compact selectedLanguage={lang} handleLanguageChange={onLanguageChange} />
        </div>
        <div className="bp-hero__body">
          <LogoTile src={info.logoUrl} name={brand} />
          <h1 className="bp-hero__brand">{brand}</h1>
          <p className="bp-hero__count">{t.stores(branches.length)}</p>
        </div>
      </header>

      <section className="bp-sheet" aria-labelledby="bp-choose">
        {last && (
          <a className="bp-continue" href={withLang(`/${last.slug}`, lang)}>
            <span className="bp-continue__text">
              <span className="bp-continue__label">{t.continueAt}</span>
              <span className="bp-continue__name">{displayName(last, lang)}</span>
            </span>
            <FontAwesomeIcon icon={faChevronRight} className="bp-continue__chev" aria-hidden="true" />
          </a>
        )}

        <h2 id="bp-choose" className="bp-sheet__title">{t.choose}</h2>

        {(canLocate || branches.length >= SEARCH_FROM) && (
          <div className="bp-tools">
            {canLocate && (
              <button
                type="button"
                className="bp-near"
                onClick={locate}
                aria-pressed={geo.status === 'located'}
                disabled={geo.status === 'locating'}
              >
                <FontAwesomeIcon icon={faLocationArrow} aria-hidden="true" />
                {geo.status === 'locating' ? t.locating : t.nearest}
              </button>
            )}
            {branches.length >= SEARCH_FROM && (
              <label className="bp-search">
                <FontAwesomeIcon icon={faMagnifyingGlass} className="bp-search__icon" aria-hidden="true" />
                <span className="visually-hidden">{t.search}</span>
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t.searchHint}
                  autoComplete="off"
                />
              </label>
            )}
          </div>
        )}

        <p className="bp-msg" role="status">{message}</p>

        {listed.length > 0 ? (
          <ul className="bp-list" aria-labelledby="bp-choose">
            {listed.map((b) => (
              <li key={b.slug}>
                <a className="bp-row" href={withLang(`/${b.slug}`, lang)}>
                  <span className="bp-row__main">
                    <span className="bp-row__name">{displayName(b, lang)}</span>
                    {b.addressText && <span className="bp-row__addr">{b.addressText}</span>}
                    <OpenStatus hours={b.hours} now={clock} lang={lang} />
                  </span>
                  {b.distanceKm !== undefined && <span className="bp-row__km">{formatKm(b.distanceKm, lang)}</span>}
                  <FontAwesomeIcon icon={faChevronRight} className="bp-row__chev" aria-hidden="true" />
                </a>
              </li>
            ))}
          </ul>
        ) : (
          <p className="bp-empty">{t.none(query.trim())}</p>
        )}
      </section>
    </main>
  );
};

export default BrandPicker;
